import Keycloak from "keycloak-js"
import axios from "axios"

// Utils
import { config } from "Utils/config"

/**
 * Persiste le realm SSO (+ l'id_token) en localStorage.
 *
 * Nécessaire car `keycloakClientInstance` (plus bas) est une simple variable
 * EN MÉMOIRE : elle repart à `null` à tout rechargement de page qui ne
 * redémarre pas sur `/client/<realm>` (ex. F5 pendant que l'utilisateur
 * navigue ailleurs dans l'app). Sans ce marqueur persistant, un utilisateur
 * pourtant toujours connecté en SSO redeviendrait indétectable comme tel :
 * `isKeycloakSession()` retomberait à `false` et le logout retomberait sur
 * `/user` au lieu du logout Keycloak.
 *
 * L'`id_token` est persisté séparément du realm : c'est ce que Keycloak exige
 * en paramètre `id_token_hint` pour fermer une session SSO sans passer par le
 * SDK (`keycloak.logout()`, indisponible si `keycloakClientInstance` a été
 * perdue) — ce serveur Keycloak refuse l'alternative spec `client_id` +
 * `post_logout_redirect_uri` seule ("Missing parameters: id_token_hint",
 * vérifié en environnement réel). Même famille de risque que `accessToken`,
 * déjà stocké en clair en localStorage par ce projet (`Login/saga.js`).
 */
export const KEYCLOAK_REALM_STORAGE_KEY = "keycloakRealm"
export const KEYCLOAK_ID_TOKEN_STORAGE_KEY = "keycloakIdToken"

function persistSsoSession(realm, idToken) {
  localStorage.setItem(KEYCLOAK_REALM_STORAGE_KEY, realm)
  if (idToken) localStorage.setItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY, idToken)
}

function getPersistedSsoRealm() {
  return localStorage.getItem(KEYCLOAK_REALM_STORAGE_KEY)
}

function getPersistedSsoIdToken() {
  return localStorage.getItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY)
}

/**
 * À appeler à la connexion email/mot de passe réussie, pour ne pas laisser un
 * marqueur SSO périmé si l'utilisateur change de mode de connexion sur ce
 * même navigateur — après ça, `getLoginPath()` doit redevenir `/user`.
 */
function clearSsoSession() {
  localStorage.removeItem(KEYCLOAK_REALM_STORAGE_KEY)
  localStorage.removeItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY)
}

/**
 * À appeler après usage de l'id_token pour un logout Keycloak : il est à
 * usage unique (une session Keycloak fermée ne peut pas être refermée avec le
 * même jeton), donc inutile à garder. Le realm, lui, N'EST PAS effacé ici
 * (contrairement à `clearSsoSession()`) : un visiteur qui se déconnecte
 * explicitement reste un utilisateur SSO connu pour cette session navigateur
 * — `getLoginPath()`/le guard de route (`Private.routes.js`) doivent
 * continuer à le renvoyer vers `/client/<realm>`, pas vers `/user`, s'il
 * revisite une page neutre ensuite sans s'être reconnecté en email/mdp.
 */
function clearSsoIdToken() {
  localStorage.removeItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY)
}

/**
 * Extrait le realm de `/client/<realm>` et le NORMALISE EN MINUSCULES.
 *
 * Keycloak identifie ses realms par correspondance exacte dans l'URL
 * (`/realms/<realm>/…`) : `Uptevia` et `uptevia` sont deux realms distincts, et
 * un nom à la mauvaise casse répond 404. Tous les realms de la plateforme sont
 * en minuscules — un ancien lien capitalisé (`/client/Uptevia`) menait donc à
 * une page d'erreur Keycloak, sans message applicatif : le `catch` de
 * `getKeycloakClient` se contente d'un `console.error` et `renderApp` n'est
 * jamais appelé.
 *
 * Normaliser ici couvre toute la chaîne, qui dérive de cette seule valeur :
 * la config Keycloak, le POST `/keycloak/login`, et le `redirectUri` du
 * `logout` (qui relit `keycloakClientInstance.realm`). Effet de bord voulu :
 * après l'aller-retour Keycloak, l'URL revient en minuscules — le vieux
 * signet se répare de lui-même.
 *
 * ⚠️ Suppose que AUCUN realm Keycloak ne porte de majuscule. Si un realm
 * capitalisé devait être créé, c'est cette normalisation qui le casserait.
 */
export function extractRealmToLocation(location) {
  const realmLocation = location?.split("/")?.[2]
  return realmLocation?.toLowerCase()
}

/**
 * Instance Keycloak conservée pour toute la durée de vie de la SPA.
 * Renseignée par `getKeycloakClient` au boot ; nécessaire pour pouvoir
 * déclencher une déconnexion SSO (`logout`) qui, elle, a besoin de la même
 * instance authentifiée (elle porte le realm et le contexte de session).
 */
let keycloakClientInstance = null

/**
 * Initialise et authentifie un client Keycloak.
 * Si l'authentification est réussie, l'utilisateur est récupéré et transmis à l'app pour la mise en store redux.
 * Si l'authentification échoue ou si une erreur se produit, le callback est appelé avec null ou l'erreur est consignée dans la console.
 *
 * @param {Func} renderApp - initialise le render de l'app.
 */
async function getKeycloakClient(renderApp) {
  const realm = extractRealmToLocation(window.location.pathname)

  if (realm) {
    const keycloakConfig = {
      url: "https://keycloak.pocketresult.com/",
      clientId: "pocketstudio",
      checkLoginIframe: false,
      realm: realm,
    }

    const keycloakClient = new Keycloak(keycloakConfig)
    keycloakClientInstance = keycloakClient

    try {
      const keycloakLoginResponse = await initKeycloakClient(keycloakClient, realm)

      if (keycloakLoginResponse) {
        persistSsoSession(realm, keycloakClient.idToken)
        renderApp(keycloakLoginResponse)
      } else {
        renderApp(null)
      }
    } catch (error) {
      console.error("Erreur lors de la procédure d'authentification: ", error)
    }
  }
}

/**
 * Initialise le client Keycloak.
 * Si l'utilisateur est authentifié, appelle la fonction 'keycloakLogin' pour échanger le token keycloak contre un utilisateur connu.
 * Retourne les données obtenues du backend si l'utilisateur est authentifié, sinon retourne null.
 *
 * @param {Object} keycloakClient - Une instance du client Keycloak.
 * @param {String} realm - Le domaine (realm) utilisé pour l'authentification avec Keycloak.
 * @returns {Promise<Object|null>} - Retourne un objet contenant les données de l'utilisateur authentifié ou null si l'authentification échoue.
 */
async function initKeycloakClient(keycloakClient, realm) {
  try {
    const authenticated = await keycloakClient.init({
      onLoad: "login-required",
      checkLoginIframe: false,
    })

    if (authenticated) {
      const keycloakToken = keycloakClient.token
      const login = await keycloakLogin(keycloakToken, realm)

      return login.data
    } else {
      console.warn("Non authentifié")
      return null
    }
  } catch (error) {
    console.error("Erreur lors de l'initialisation de Keycloak: ", error)
    throw error
  }
}

/**
 * Envoie une requête au backend avec le token Keycloak pour authentifier l'utilisateur et obtenir des informations supplémentaires.
 * En cas de réponse valide de l'API (status 200 et données présentes), retourne les données.
 * En cas d'échec de la requête ou de réponse invalide, consigne une erreur et retourne null.
 *
 * @param {String} keycloakToken - Le token d'accès Keycloak de l'utilisateur.
 * @param {String} realm - Le domaine (realm) utilisé pour l'authentification avec Keycloak.
 * @returns {Promise<Object|null>} - Retourne un objet contenant la réponse de l'API en cas de succès, sinon retourne null.
 */
async function keycloakLogin(keycloakToken, realm) {
  try {
    const response = await axios.post(`${config.API_URL}/keycloak/login`, {
      keycloakToken,
      realm,
    })

    if (response.data && response.status === 200) {
      return {
        data: response.data,
      }
    } else {
      console.error("Réponse invalide de l'API", response)
      return null
    }
  } catch (error) {
    console.error("Erreur lors de la connexion Keycloak à Studio", error)
    throw error
  }
}

/** Chemin de la page de login applicative classique (email/mot de passe). */
const LOGIN_PATH = "/user"

/**
 * Realm SSO de la session courante, ou `null` si l'utilisateur n'est pas
 * connecté en SSO. Source de vérité UNIQUE utilisée par `isKeycloakSession()`,
 * `getLoginPath()` et `logout()` — priorité au client Keycloak vivant, repli
 * sur le marqueur persisté (`getPersistedSsoRealm`) si `keycloakClientInstance`
 * a été perdue par un rechargement de page qui n'est pas reparti de
 * `/client/<realm>` (ex. F5 pendant que l'utilisateur navigue ailleurs dans
 * l'app) — sinon un utilisateur SSO redeviendrait indétectable comme tel
 * après un simple F5.
 *
 * @returns {string|null}
 */
function getSsoRealm() {
  if (keycloakClientInstance && keycloakClientInstance.authenticated) {
    return keycloakClientInstance.realm
  }
  return getPersistedSsoRealm()
}

/**
 * Indique si l'utilisateur courant s'est connecté via Keycloak (SSO).
 * Sert à distinguer, à la déconnexion, une session SSO (→ logout Keycloak)
 * d'une session de login applicative classique (→ redirection /user).
 *
 * @returns {boolean}
 */
function isKeycloakSession() {
  return Boolean(getSsoRealm())
}

/**
 * Chemin vers lequel envoyer un visiteur non authentifié : `/client/<realm>`
 * s'il a une session SSO connue (live ou persistée), sinon `/user`.
 *
 * Fonction PURE (aucun effet de bord, n'appelle jamais `logout()`) :
 * utilisable en render (guards de route, ex. `Private.routes.js`), sans
 * risque de déclencher un effet de bord à chaque re-render.
 *
 * @returns {string}
 */
function getLoginPath() {
  const realm = getSsoRealm()
  return realm ? `/client/${realm}` : LOGIN_PATH
}

/**
 * Construit l'URL de fin de session Keycloak (RP-Initiated Logout), le même
 * appel que fait le SDK en interne (`keycloak.createLogoutUrl`), à la
 * différence qu'elle ne dépend d'aucune instance de client vivante — juste du
 * realm et de l'`id_token` persistés. Fonction PURE, séparée de `logout()`
 * pour rester testable sans dépendre de `window.location`.
 *
 * @param {string} realm
 * @param {string} idToken
 * @param {string} redirectUri
 * @returns {string}
 */
export function buildKeycloakLogoutUrl(realm, idToken, redirectUri) {
  return (
    `https://keycloak.pocketresult.com/realms/${realm}/protocol/openid-connect/logout` +
    `?id_token_hint=${encodeURIComponent(idToken)}` +
    `&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`
  )
}

/**
 * Déconnexion SSO Keycloak.
 *
 * Cas nominal : `keycloakClientInstance` est vivante (l'app a démarré sur
 * `/client/<realm>` dans cet onglet). On appelle `keycloak.logout()`, qui
 * redirige le navigateur vers l'endpoint `end_session` de Keycloak — la
 * session SSO est réellement terminée, puis Keycloak renvoie vers
 * `redirectUri`. On revient sur `/client/<realm>`, ce qui déclenche à nouveau
 * `init({ onLoad: "login-required" })` dans `getKeycloakClient` — la session
 * étant close, Keycloak réaffiche sa page de login (et non la page de login
 * applicative).
 *
 * Cas de secours : `keycloakClientInstance` a été perdue (rechargement de
 * page pendant la navigation dans l'app) mais realm + id_token sont
 * persistés (`getKeycloakClient`). On reconstruit la même URL `end_session`
 * que le SDK aurait construite (`buildKeycloakLogoutUrl`) — vraie fermeture
 * de session côté IdP, sans dépendre d'un client vivant.
 *
 * Repli dégradé : realm persisté mais pas d'id_token (session ouverte avant
 * ce correctif, ou perdu). On ne peut alors pas garantir la fermeture de la
 * session IdP : on renvoie sur `/client/<realm>`, qui relance
 * `getKeycloakClient` → si la session Keycloak est encore active,
 * l'utilisateur est reconnecté silencieusement (l'état applicatif local, lui,
 * a bien été purgé) ; sinon Keycloak affiche sa page de login. Dans tous les
 * cas, c'est la bonne porte (SSO), pas `/user`.
 *
 * @returns {boolean} true si une redirection SSO a été déclenchée.
 */
function logout() {
  if (keycloakClientInstance) {
    const realm = keycloakClientInstance.realm
    const redirectUri = `${window.location.origin}/client/${realm}`

    keycloakClientInstance.logout({ redirectUri })
    clearSsoIdToken()
    return true
  }

  const persistedRealm = getPersistedSsoRealm()
  const persistedIdToken = getPersistedSsoIdToken()
  if (persistedRealm && persistedIdToken) {
    const redirectUri = `${window.location.origin}/client/${persistedRealm}`

    clearSsoIdToken()
    window.location.href = buildKeycloakLogoutUrl(persistedRealm, persistedIdToken, redirectUri)
    return true
  }

  if (persistedRealm) {
    window.location.href = `${window.location.origin}/client/${persistedRealm}`
    return true
  }

  return false
}

/**
 * Point d'entrée unique de déconnexion pour les déclencheurs IMPÉRATIFS
 * (bouton de déconnexion, timeout d'inactivité) : déclenche le vrai logout
 * Keycloak si la session est SSO, sinon indique le chemin de repli vers
 * lequel l'appelant doit naviguer. Pendant du `getLoginPath()` pour ces deux
 * déclencheurs (le 3e, les guards de route, utilise `getLoginPath()`
 * directement — pas d'effet de bord possible en render).
 *
 * @returns {string|null} le chemin vers lequel naviguer, ou `null` si
 * `logout()` a déjà déclenché une navigation complète (redirection SSO en
 * cours) — l'appelant n'a alors rien de plus à faire.
 */
function performLogout() {
  if (isKeycloakSession() && logout()) return null
  return LOGIN_PATH
}

const KeycloakService = {
  getKeycloakClient,
  isKeycloakSession,
  getLoginPath,
  performLogout,
  logout,
  clearSsoSession,
}

export default KeycloakService
